import {
  getActiveOwnerPortal,
  getActiveOwnerPortalApiHost,
  hasActiveOwnerPortal,
} from '../owner-portal';
// import type { paths } from './api.generated  ';
import { showErrorNotification } from '@vrplatform/theme';
import {
  showWarnNotification,
  updateErrorNotification,
} from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import * as z from 'zod';
import {
  type InfiniteData,
  type QueryKey as TanstackQueryKey,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  useInfiniteQuery as useTanstackInfiniteQuery,
  useMutation,
  useQuery as useTanstackQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  useApiClient as createApiFetchClient,
  getControlPlaneRequestUrl,
  isRequestRegionRoute,
  retargetRequest,
  type ApiClientError,
} from '@vrplatform/api';
import type {
  Client as OpenApiFetchClient,
  FetchResponse,
  MaybeOptionalInit,
} from 'openapi-fetch';
import createClient from 'openapi-react-query';
import type {
  HttpMethod,
  MediaType,
  PathsWithMethod,
} from 'openapi-typescript-helpers';
import {
  getTransportErrorKind,
  retry,
  retryIdempotentMethod,
} from './api.retry';
import {
  getApiQueryErrorSentryMetadata,
  isClerkOfflineErrorMessage,
  isClerkSessionUnavailableErrorMessage,
  isInvalidJwtApiError,
  isInvalidJwtApiErrorBody,
  parseVrpApiBaseUrl,
  shouldCaptureApiQueryError,
} from './api.utils';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { PLATFORM } from '../env';
import type { QueryKey } from '../graphql';
import {
  captureSentryError,
  transportErrorCapturePolicy,
  type PollUntilCompletedProps,
  useMe,
  usePartnerRegion,
  usePollUntilCompleted,
  useTeam,
  useTeamRole,
} from '../hooks';
import { isStorybook } from '../env';
import {
  useLocalStorage,
  useNetwork,
  readLocalStorageValue,
} from '@mantine/hooks';
import {
  getAuditActionNotificationStorageKey,
  showAuditActionNotification,
} from '../notification';
import type {
  ApiPaths,
  ClientMethodForPaths,
  InitParam,
  InitWithUnknowns,
  MethodForPath,
  OperationForPath,
} from './api.types';
import type { DataRegion } from '../hooks/team/team.types';
import { getPreviewApiHost, VRP_TENANT_ID } from '@vrplatform/ui-common';

const API_ENDPOINT_STORAGE_KEY = 'api-endpoint';

const getDefaultApiHost = () => {
  if (typeof window === 'undefined') return 'api.vrplatform.app';

  const hostname = window.location.hostname;

  // A validated customer portal talks to the API of its lane; an unrecognised
  // hostname never reaches production on its own.
  if (hasActiveOwnerPortal()) return getActiveOwnerPortalApiHost();
  if (hostname === 'portal.vrplatform.app') return 'api.vrplatform.app';

  // A pull request preview talks to its own API and database, never to staging.
  const previewApiHost = getPreviewApiHost(hostname);
  if (previewApiHost) return previewApiHost;

  if (import.meta.env.VITE_API_HOST) {
    return import.meta.env.VITE_API_HOST;
  }

  if (
    hostname === 'localhost' ||
    hostname === 'staging.portal.vrplatform.app' ||
    hostname.includes('app-256.pages.dev')
  ) {
    return 'staging.api.vrplatform.app';
  }

  return 'api.vrplatform.app';
};

const normalizeApiHost = (value?: string | null) => {
  if (!value) return undefined;

  const trimmed = value.replaceAll('"', '').trim();
  if (!trimmed) return undefined;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      return new URL(trimmed).host;
    } catch {
      return trimmed;
    }
  }

  return trimmed;
};

const resolveApiHost = (value?: string | null) => {
  if (hasActiveOwnerPortal()) return getActiveOwnerPortalApiHost();
  // A stored host never takes a pull request preview to another API.
  const previewApiHost =
    typeof window === 'undefined'
      ? undefined
      : getPreviewApiHost(window.location.hostname);
  if (previewApiHost) return previewApiHost;
  const normalizedApiHost = normalizeApiHost(value);

  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'portal.vrplatform.app' &&
    normalizedApiHost === 'staging.api.vrplatform.app'
  ) {
    return getDefaultApiHost();
  }

  // PRO-20018: the staging UI needs the staging API contract, so a stored
  // production host is ignored there. The local API stays available.
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'staging.portal.vrplatform.app' &&
    normalizedApiHost !== undefined &&
    normalizedApiHost !== 'staging.api.edge.vrplatform.app' &&
    normalizedApiHost !== 'localhost:8877'
  ) {
    return getDefaultApiHost();
  }

  return normalizedApiHost ?? getDefaultApiHost();
};

const getApiBaseUrl = (host: string) => {
  const protocol = host.includes('localhost') ? 'http' : 'https';
  return host.startsWith('http://') || host.startsWith('https://')
    ? host
    : `${protocol}://${host}`;
};

export type api = ApiPaths;

export { ApiClientError } from '@vrplatform/api';

/**
 * The team-scoped request decorator the typed API client already uses. Exposed
 * for the few endpoints whose generated operation cannot model their request
 * body, so they authenticate identically instead of rebuilding header logic.
 */
export type AuthenticatedApiFetch = (request: Request) => Promise<Response>;

type ApiContext = Client & {
  authenticatedFetch: AuthenticatedApiFetch;
  controlPlaneTeamApi: VrpApiClient;
  crossTeamApi: VrpApiClient;
  crossTeamApiHost: string;
  getRegionalCrossTeamApi: (apiBaseUrl: string) => VrpApiClient;
  regionalCrossTeamApi: VrpApiClient;
  singleAttemptApi: VrpApiClient;
  dataRegion: DataRegion | null | undefined;
  teamId: string | null | undefined;
  isTeamIdFetching: boolean;
  apiHost: string;
  baseUrl: string;
};

const context = createContext<ApiContext>({
  queryOptions: undefined,
  useMutation: undefined,
  useQuery: undefined,
  useSuspenseQuery: undefined,
  apiHost: '',
  baseUrl: '',
  teamId: undefined,
} as any);

type VrpApiClient = OpenApiFetchClient<ApiPaths> & { sessionId: string };

type Client = ReturnType<typeof createClient<ApiPaths>> & {
  $api: VrpApiClient;
};

export type ApiTarget = 'regional' | 'controlPlane';

type ApiRequestOptions = {
  apiTarget?: ApiTarget;
  hideAuditActionNotification?: boolean;
  teamScoped?: boolean;
};

type CapturedRegionalApiRequestOptions = ApiRequestOptions & {
  regionalApiBaseUrl?: string;
};

const misdirectedRequestSchema = z.object({
  context: z.object({ apiBaseUrl: z.string() }),
});

const getCapturedRegionalApiHost = (apiBaseUrl: string) => {
  let url: URL;

  try {
    url = new URL(apiBaseUrl);
  } catch {
    throw new Error('Captured regional API base URL must be a valid URL');
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error('Captured regional API base URL must use HTTP or HTTPS');
  }

  return url.host;
};

const getApiRequestContext = (
  apiContext: ApiContext,
  options: CapturedRegionalApiRequestOptions
) => {
  const teamScoped = options.teamScoped ?? true;
  const apiTarget =
    options.apiTarget ??
    (teamScoped && apiContext.teamId !== VRP_TENANT_ID
      ? 'regional'
      : 'controlPlane');

  if (
    options.regionalApiBaseUrl !== undefined &&
    (teamScoped || apiTarget !== 'regional')
  ) {
    throw new Error(
      'Captured regional API base URL requires a non-team-scoped regional request'
    );
  }

  const capturedRegionalApiHost = options.regionalApiBaseUrl
    ? getCapturedRegionalApiHost(options.regionalApiBaseUrl)
    : undefined;
  const api =
    apiTarget === 'regional'
      ? teamScoped
        ? apiContext.$api
        : options.regionalApiBaseUrl
          ? apiContext.getRegionalCrossTeamApi(options.regionalApiBaseUrl)
          : apiContext.regionalCrossTeamApi
      : teamScoped
        ? apiContext.controlPlaneTeamApi
        : apiContext.crossTeamApi;
  const apiHost =
    apiTarget === 'regional'
      ? capturedRegionalApiHost &&
        apiContext.crossTeamApiHost !== 'localhost:8877'
        ? capturedRegionalApiHost
        : apiContext.apiHost
      : apiContext.crossTeamApiHost;
  const dataRegion =
    teamScoped && apiTarget === 'regional' ? apiContext.dataRegion : undefined;
  const teamId = teamScoped ? apiContext.teamId : undefined;

  return { api, apiHost, apiTarget, dataRegion, teamId, teamScoped };
};

export const QUERY_GROUPS = [
  'accounts',
  'apps',
  'automations',
  'bank-records',
  'bank-rules',
  'bank-transfers',
  'connections',
  'deposits',
  'expenses',
  'fees',
  'files',
  'listings',
  'members',
  'operations',
  'owners',
  'reports',
  'reservations',
  'owner-statements',
  'owner-statement-navigation',
  'platform-defaults',
  'pm-statements',
  'teams',
  'settings',
] as const;

export type QueryGroup = (typeof QUERY_GROUPS)[number];

export type AuthenticatedRequestFailureHandler = (
  status: 401 | 403,
  accessToken: string
) => boolean | Promise<boolean>;

type ApiMethodForPath<
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
> = MethodForPath<ApiPaths, Method, Path>;

type ApiOperation<
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
> = OperationForPath<ApiPaths, Method, Path>;

type ApiSuccessResponse<
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
  Init extends MaybeOptionalInit<
    ApiPaths[Path],
    ApiMethodForPath<Method, Path>
  >,
  Media extends MediaType = MediaType,
> = Extract<
  FetchResponse<ApiOperation<Method, Path>, Init, Media>,
  { data: any }
>;

type ApiErrorResponse<
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
  Init extends MaybeOptionalInit<
    ApiPaths[Path],
    ApiMethodForPath<Method, Path>
  >,
  Media extends MediaType = MediaType,
> = Extract<
  FetchResponse<ApiOperation<Method, Path>, Init, Media>,
  { error: any }
>['error'];

const isApiSuccessResponse = <
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
  Init extends MaybeOptionalInit<
    ApiPaths[Path],
    ApiMethodForPath<Method, Path>
  >,
>(
  response: FetchResponse<ApiOperation<Method, Path>, Init, MediaType>
): response is ApiSuccessResponse<Method, Path, Init> => 'data' in response;

export type UseMutationWithResponseMethod<
  Paths extends Record<string, Record<HttpMethod, {}>>,
  Media extends MediaType,
> = <
  Method extends HttpMethod,
  Path extends PathsWithMethod<Paths, Method>,
  Init extends MaybeOptionalInit<Paths[Path], Method>,
  Response extends Required<
    Extract<
      FetchResponse<NonNullable<Paths[Path][Method]>, Init, Media>,
      {
        data: any;
      }
    >
  >,
  Error extends Extract<
    FetchResponse<NonNullable<Paths[Path][Method]>, Init, Media>,
    { error: any }
  >['error'],
  Options extends Omit<
    UseMutationOptions<Response, Error, Init>,
    'mutationKey' | 'mutationFn'
  >,
>(
  method: Method,
  url: Path,
  options?: Options,
  queryClient?: QueryClient
) => UseMutationResult<Response, Error, Init>;

export const useApiEndpoint = () => {
  const [storedApiHost, setStoredApiHost, removeStoredApiHost] =
    useLocalStorage<string | undefined>({
      key: API_ENDPOINT_STORAGE_KEY,
      getInitialValueInEffect: false,
    });

  const apiHost = useMemo(() => resolveApiHost(storedApiHost), [storedApiHost]);
  const baseUrl = useMemo(() => getApiBaseUrl(apiHost), [apiHost]);

  const setApiHost = useCallback(
    (value?: string) => {
      if (!value) {
        removeStoredApiHost();
        return;
      }
      setStoredApiHost(value);
    },
    [removeStoredApiHost, setStoredApiHost]
  );

  return { apiHost, baseUrl, setApiHost, storedApiHost };
};

export const getApiErrorMessage = (
  error: ApiClientError,
  defaultMessage: string
) =>
  error?.issues?.[0]?.message ||
  error?.error?.message ||
  error?.error?.[0]?.message ||
  error?.message ||
  defaultMessage;

export const showApiErrorNotification = ({
  error,
  title,
  defaultMessage = 'Please try again later and contact support if the issue persists.',
  id,
}: {
  error: any;
  title: string;
  defaultMessage?: string;
  id?: string;
}) => {
  const message = getApiErrorMessage(error, defaultMessage);

  if (id) {
    updateErrorNotification({
      id,
      title,
      message,
    });
  } else {
    showErrorNotification({
      title,
      message,
    });
  }
};

const isRegionAwarePortfolioRead = (request: {
  method: string;
  url: string;
}) => {
  if (request.method !== 'GET') return false;

  const pathname = new URL(request.url).pathname;

  return (
    pathname === '/teams' ||
    pathname === '/teams/issues' ||
    pathname === '/teams/lookup' ||
    pathname === '/partner/users' ||
    /^\/partner\/users\/[^/]+$/.test(pathname)
  );
};

// PRO-12793: keep the malformed-token guard for the normal app path, while
// allowing opaque server-issued embed sessions.
const requireAccessToken = (
  token: Maybe<string>,
  allowOpaqueAccessToken: boolean
) => {
  const normalizedAccessToken = typeof token === 'string' ? token.trim() : '';
  const hasUsableAccessToken =
    !!normalizedAccessToken &&
    normalizedAccessToken !== 'undefined' &&
    normalizedAccessToken !== 'null';

  if (
    !hasUsableAccessToken ||
    (!allowOpaqueAccessToken && normalizedAccessToken.split('.').length !== 3)
  ) {
    const error = new Error('No API access');
    // The retry wrapper preserves Error.name, but drops custom error codes.
    error.name =
      token === null || token === undefined
        ? 'ApiSessionUnavailableError'
        : 'ApiInvalidAccessTokenError';
    throw error;
  }

  return normalizedAccessToken;
};

const toAuthorizationHeader = (accessToken: string) =>
  accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;

/**
 * PRO-17358: one shared forced refresh identifies one recovery attempt. Keying
 * the diagnostic on that promise reports a credential that still fails after a
 * genuine refresh exactly once, however many parallel requests waited on it.
 */
const reportedAuthRecoveryFailures = new WeakSet<Promise<Maybe<string>>>();

const AUTH_RECOVERY_COOLDOWN_MS = 60_000;

/**
 * PRO-17358: a session rejected for a reason no refresh can fix would otherwise
 * mint a fresh Clerk token and duplicate every request on each poll and each
 * window-focus refetch, indefinitely. Once a refreshed credential is itself
 * rejected, stop recovering for a while and let requests fail terminally.
 *
 * Keyed on the refresh callback, so one cooldown covers every provider sharing
 * a session token provider, and a new session starts from a clean slate.
 */
const authRecoveryCooldowns = new WeakMap<object, number>();

const isAuthRecoveryOnCooldown = (forceRefresh: object) => {
  const cooldownUntil = authRecoveryCooldowns.get(forceRefresh);

  if (cooldownUntil === undefined) return false;
  if (Date.now() < cooldownUntil) return true;

  authRecoveryCooldowns.delete(forceRefresh);

  return false;
};

/**
 * PRO-17358: only the API's auth-middleware rejection is replayable. It is
 * refused before any route handler runs, so the original request cannot have
 * reached a side effect. Any other 401 — a route-level decision, a proxy, an
 * edge error page — may already have done work, and a fresh credential would
 * not change the outcome.
 */
const isReplayableAuthRejection = async (response: Response) => {
  try {
    return isInvalidJwtApiErrorBody(await response.clone().json());
  } catch {
    return false;
  }
};

export const ApiClientProvider = ({
  allowOpaqueAccessToken = false,
  baseUrlOverride,
  children,
  dataRegion,
  dataRegionBaseUrl,
  getToken,
  onAuthenticatedRequestFailure,
  onDataRegionInvalid,
  refreshToken,
  teamBaseUrlOverride,
  teamId,
  isTeamIdFetching,
  attempts = 3,
}: {
  allowOpaqueAccessToken?: boolean;
  baseUrlOverride?: string | null;
  children: React.ReactNode;
  dataRegion?: DataRegion | null;
  /** The API of `dataRegion`, which partner sessions call by default. */
  dataRegionBaseUrl?: string | null;
  getToken: () => Promise<Maybe<string>>;
  onAuthenticatedRequestFailure?: AuthenticatedRequestFailureHandler;
  onDataRegionInvalid?: () => void | Promise<void>;
  /**
   * Forces a fresh credential, bypassing any client-side token cache. Provide
   * it to opt a client into single-replay recovery from a rejected session;
   * clients without it keep failing on the first 401. When the forced refresh
   * yields no usable token the request throws `No API access` and terminal
   * `onAuthenticatedRequestFailure` handling does not run for it.
   */
  refreshToken?: () => Promise<Maybe<string>>;
  teamBaseUrlOverride?: string | null;
  teamId: string | null | undefined;
  isTeamIdFetching: boolean;
  attempts?: number;
}) => {
  const endpoint = useApiEndpoint();
  const controlPlaneApiHost = useMemo(
    () =>
      baseUrlOverride
        ? (normalizeApiHost(baseUrlOverride) ?? endpoint.apiHost)
        : endpoint.apiHost,
    [baseUrlOverride, endpoint.apiHost]
  );
  const controlPlaneBaseUrl = useMemo(
    () => (baseUrlOverride ? getApiBaseUrl(baseUrlOverride) : endpoint.baseUrl),
    [baseUrlOverride, endpoint.baseUrl]
  );
  const teamBaseUrl = useMemo(
    () =>
      controlPlaneApiHost === 'localhost:8877' || !teamBaseUrlOverride
        ? controlPlaneBaseUrl
        : getApiBaseUrl(teamBaseUrlOverride),
    [controlPlaneApiHost, controlPlaneBaseUrl, teamBaseUrlOverride]
  );
  const baseUrl = useMemo(
    () =>
      controlPlaneApiHost === 'localhost:8877' || !dataRegionBaseUrl
        ? teamBaseUrl
        : getApiBaseUrl(dataRegionBaseUrl),
    [controlPlaneApiHost, dataRegionBaseUrl, teamBaseUrl]
  );
  const apiHost = useMemo(
    () => normalizeApiHost(baseUrl) ?? controlPlaneApiHost,
    [baseUrl, controlPlaneApiHost]
  );
  const queryClient = useQueryClient();
  const previousControlPlaneApiHost = useRef<string | null>(null);
  const onAuthenticatedRequestFailureRef = useRef(
    onAuthenticatedRequestFailure
  );
  const onDataRegionInvalidRef = useRef(onDataRegionInvalid);
  const refreshTokenRef = useRef(refreshToken);

  useEffect(() => {
    onAuthenticatedRequestFailureRef.current = onAuthenticatedRequestFailure;
  }, [onAuthenticatedRequestFailure]);

  useEffect(() => {
    onDataRegionInvalidRef.current = onDataRegionInvalid;
  }, [onDataRegionInvalid]);

  useEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  useEffect(() => {
    if (previousControlPlaneApiHost.current === null) {
      previousControlPlaneApiHost.current = controlPlaneApiHost;
      return;
    }

    if (previousControlPlaneApiHost.current === controlPlaneApiHost) return;

    previousControlPlaneApiHost.current = controlPlaneApiHost;
    queryClient.invalidateQueries({ refetchType: 'active' });
  }, [controlPlaneApiHost, queryClient]);

  const createAuthenticatedFetch = useCallback(
    (
      defaultTeamId: string | null | undefined,
      defaultDataRegion?: DataRegion | null
    ): AuthenticatedApiFetch =>
      async (request) => {
        const accessToken = requireAccessToken(
          await getToken(),
          allowOpaqueAccessToken
        );

        // Preserve the input type across the merged browser/Workers clone declarations.
        let authenticatedRequest = request.clone() as typeof request;
        const existingHeaderTenantId =
          authenticatedRequest.headers.get('x-team-id');
        const headerTenantId = existingHeaderTenantId || defaultTeamId;
        const url = getControlPlaneRequestUrl(
          request.method,
          new URL(request.url)
        );
        // PRO-19432: a partner session calls its selected region's API, but
        // the API places requests for the partner's own team data by the
        // partner team's region. Those go to the partner team's own API.
        if (
          defaultDataRegion &&
          headerTenantId === defaultTeamId &&
          url.origin === new URL(baseUrl).origin &&
          !isRequestRegionRoute(request.method, url)
        ) {
          const teamUrl = new URL(teamBaseUrl);
          url.protocol = teamUrl.protocol;
          url.host = teamUrl.host;
        }
        if (url.href !== request.url)
          authenticatedRequest = await retargetRequest(
            authenticatedRequest,
            url
          );

        authenticatedRequest.headers.set(
          'Authorization',
          toAuthorizationHeader(accessToken)
        );
        const portal = getActiveOwnerPortal();
        if (portal)
          authenticatedRequest.headers.set('x-owner-portal', portal.hostname);
        else authenticatedRequest.headers.delete('x-owner-portal');
        if (headerTenantId)
          authenticatedRequest.headers.set('x-team-id', headerTenantId);
        if (headerTenantId && defaultDataRegion) {
          authenticatedRequest.headers.set('x-data-region', defaultDataRegion);
        }
        // Keep the generated value locally so recovery telemetry never has
        // to trust or reread an arbitrary caller-provided header.
        const transactionId = Math.random().toString(36).substring(2, 9);
        authenticatedRequest.headers.set('x-transaction-id', transactionId);
        authenticatedRequest.headers.set('platform', PLATFORM);

        const forceRefresh = refreshTokenRef.current;
        // PRO-17358: fetching consumes the request body, so keep a replayable
        // copy while the credential is still unproven.
        const replayableRequest = forceRefresh
          ? authenticatedRequest.clone()
          : null;

        let activeAccessToken = accessToken;
        let response = await globalThis.fetch(
          authenticatedRequest as unknown as Parameters<
            typeof globalThis.fetch
          >[0]
        );

        // PRO-17358: replaying the request once with a forced-refresh
        // credential recovers an expired or stale session. Only the API's
        // `Invalid JWT` rejection qualifies: it is refused in auth middleware
        // before any route handler runs, so the replay cannot repeat a side
        // effect, and it is the one rejection a new credential can fix. Every
        // other 401, and every 403, is returned untouched.
        if (
          response.status === 401 &&
          forceRefresh &&
          replayableRequest &&
          !isAuthRecoveryOnCooldown(forceRefresh) &&
          (await isReplayableAuthRejection(response))
        ) {
          // Concurrent requests can share this promise, so this measures how
          // long this request waited rather than claiming full refresh time.
          const refreshWaitStartedAt = globalThis.performance.now();
          const forcedRefresh = forceRefresh();
          const refreshedAccessToken = await forcedRefresh;
          const measuredRefreshWaitDurationMs = Math.round(
            globalThis.performance.now() - refreshWaitStartedAt
          );
          const refreshWaitDurationMs = Number.isFinite(
            measuredRefreshWaitDurationMs
          )
            ? Math.max(0, measuredRefreshWaitDurationMs)
            : undefined;

          activeAccessToken = requireAccessToken(
            refreshedAccessToken,
            allowOpaqueAccessToken
          );
          replayableRequest.headers.set(
            'Authorization',
            toAuthorizationHeader(activeAccessToken)
          );
          response = await globalThis.fetch(
            replayableRequest as unknown as Parameters<
              typeof globalThis.fetch
            >[0]
          );

          // Mirror the first exact-body check: a route/proxy 401 is terminal,
          // but it is not proof that the refreshed JWT was rejected.
          if (
            response.status === 401 &&
            (await isReplayableAuthRejection(response))
          ) {
            authRecoveryCooldowns.set(
              forceRefresh,
              Date.now() + AUTH_RECOVERY_COOLDOWN_MS
            );

            if (!reportedAuthRecoveryFailures.has(forcedRefresh)) {
              reportedAuthRecoveryFailures.add(forcedRefresh);
              // Tags stay on a small fixed vocabulary; per-request values are
              // validated and kept in optional extras instead.
              const rawHttpMethod = replayableRequest.method.toUpperCase();
              const httpMethod =
                /^(DELETE|GET|HEAD|OPTIONS|PATCH|POST|PUT)$/.test(rawHttpMethod)
                  ? rawHttpMethod
                  : 'OTHER';
              const safeTransactionId = /^[a-z0-9]{7}$/.test(transactionId)
                ? transactionId
                : undefined;
              captureSentryError(new Error('auth-recovery-failed'), {
                fingerprint: ['api-auth-recovery-failed'],
                level: 'error',
                tags: {
                  auth_recovery_response: 'invalid-jwt',
                  error_source: 'api-auth-recovery',
                  http_method: httpMethod,
                },
                extra: {
                  refresh_attempted: true,
                  refreshed_credential_changed:
                    activeAccessToken !== accessToken,
                  ...(refreshWaitDurationMs === undefined
                    ? {}
                    : { refresh_wait_duration_ms: refreshWaitDurationMs }),
                  ...(safeTransactionId
                    ? { transaction_id: safeTransactionId }
                    : {}),
                },
              });
            }
          }
        }

        // A strict regional cell answers a team it does not host with 421 and
        // names the right cell. Replay a read once there; the replayed response
        // is returned as-is, so a second 421 surfaces as an error. Writes are
        // never replayed.
        if (
          response.status === 421 &&
          controlPlaneApiHost !== 'localhost:8877' &&
          (authenticatedRequest.method === 'GET' ||
            authenticatedRequest.method === 'HEAD')
        ) {
          const misrouted = misdirectedRequestSchema.safeParse(
            await response
              .clone()
              .json()
              .catch(() => null)
          );
          const correctApiBaseUrl = misrouted.data?.context.apiBaseUrl;

          const correctUrl = correctApiBaseUrl
            ? parseVrpApiBaseUrl(correctApiBaseUrl, controlPlaneApiHost)
            : undefined;

          if (
            correctUrl &&
            correctUrl.host !== new URL(authenticatedRequest.url).host
          ) {
            const retryUrl = new URL(authenticatedRequest.url);
            retryUrl.protocol = correctUrl.protocol;
            retryUrl.host = correctUrl.host;
            response = await globalThis.fetch(
              new Request(
                retryUrl,
                authenticatedRequest
              ) as unknown as Parameters<typeof globalThis.fetch>[0]
            );
          }
        }

        if (
          response.status === 400 &&
          defaultDataRegion &&
          isRegionAwarePortfolioRead(authenticatedRequest)
        ) {
          void onDataRegionInvalidRef.current?.();
        }

        // Terminal handling only: a recovered first 401 must not clear state.
        if (
          (response.status === 401 || response.status === 403) &&
          onAuthenticatedRequestFailureRef.current
        ) {
          const captureCallbackError = (error: unknown) => {
            captureSentryError(error, {
              tags: {
                error_source: 'authenticated-request-failure-callback',
                http_status: String(response.status),
              },
            });
          };

          try {
            const shouldClearQueries = await Promise.resolve(
              onAuthenticatedRequestFailureRef.current(
                response.status,
                activeAccessToken
              )
            );

            if (shouldClearQueries) queryClient.clear();
          } catch (error) {
            captureCallbackError(error);
          }
        }

        if (!response.ok && !(await response.clone().text()).trim()) {
          const headers = new Headers(response.headers);
          headers.delete('Content-Length');
          headers.delete('Content-Encoding');
          headers.set('Content-Type', 'application/json');
          return Response.json(
            {
              message: `API request failed with HTTP ${response.status} (empty response body).`,
              status: response.status,
              issues: [],
            },
            {
              status: response.status,
              statusText: response.statusText,
              headers,
            }
          );
        }

        return response;
      },
    [
      allowOpaqueAccessToken,
      baseUrl,
      controlPlaneApiHost,
      getToken,
      queryClient,
      teamBaseUrl,
    ]
  );

  const createAuthenticatedApiClient = useCallback(
    (
      requestBaseUrl: string,
      defaultTeamId: string | null | undefined,
      defaultDataRegion?: DataRegion | null,
      requestAttempts = attempts
    ) =>
      createApiFetchClient({
        baseUrl: requestBaseUrl,
        attempts: requestAttempts,
        fetch: createAuthenticatedFetch(defaultTeamId, defaultDataRegion),
      }) as unknown as VrpApiClient,
    [attempts, createAuthenticatedFetch]
  );

  const singleAttemptFetchClient = useMemo(
    () => createAuthenticatedApiClient(baseUrl, teamId, dataRegion, 1),
    [baseUrl, createAuthenticatedApiClient, dataRegion, teamId]
  );

  const authenticatedFetch = useMemo(
    () => createAuthenticatedFetch(teamId, dataRegion),
    [createAuthenticatedFetch, dataRegion, teamId]
  );

  const fetchClient = useMemo(
    () => createAuthenticatedApiClient(baseUrl, teamId, dataRegion),
    [baseUrl, createAuthenticatedApiClient, dataRegion, teamId]
  );
  const regionalCrossTeamApi = useMemo(
    () => createAuthenticatedApiClient(baseUrl, undefined, undefined),
    [baseUrl, createAuthenticatedApiClient]
  );
  const regionalCrossTeamApiCache = useMemo(
    () => new Map<string, VrpApiClient>(),
    [createAuthenticatedApiClient]
  );
  const getRegionalCrossTeamApi = useCallback(
    (apiBaseUrl: string) => {
      const requestBaseUrl =
        controlPlaneApiHost === 'localhost:8877'
          ? controlPlaneBaseUrl
          : getApiBaseUrl(apiBaseUrl);
      const cachedApi = regionalCrossTeamApiCache.get(requestBaseUrl);

      if (cachedApi) return cachedApi;

      const regionalApi = createAuthenticatedApiClient(
        requestBaseUrl,
        undefined,
        undefined
      );
      regionalCrossTeamApiCache.set(requestBaseUrl, regionalApi);

      return regionalApi;
    },
    [
      controlPlaneApiHost,
      controlPlaneBaseUrl,
      createAuthenticatedApiClient,
      regionalCrossTeamApiCache,
    ]
  );
  const controlPlaneTeamApi = useMemo(
    () => createAuthenticatedApiClient(controlPlaneBaseUrl, teamId, undefined),
    [controlPlaneBaseUrl, createAuthenticatedApiClient, teamId]
  );
  const crossTeamApi = useMemo(
    () =>
      createAuthenticatedApiClient(controlPlaneBaseUrl, undefined, undefined),
    [controlPlaneBaseUrl, createAuthenticatedApiClient]
  );

  const $api = useMemo(() => createClient(fetchClient), [fetchClient]);

  const value = useMemo(
    () => ({
      ...$api,
      $api: fetchClient,
      singleAttemptApi: singleAttemptFetchClient,
      authenticatedFetch,
      controlPlaneTeamApi,
      crossTeamApi,
      crossTeamApiHost: controlPlaneApiHost,
      getRegionalCrossTeamApi,
      regionalCrossTeamApi,
      dataRegion,
      apiHost,
      baseUrl,
      teamId,
      isTeamIdFetching,
    }),
    [
      $api,
      authenticatedFetch,
      fetchClient,
      singleAttemptFetchClient,
      controlPlaneTeamApi,
      crossTeamApi,
      controlPlaneApiHost,
      getRegionalCrossTeamApi,
      regionalCrossTeamApi,
      dataRegion,
      apiHost,
      baseUrl,
      teamId,
      isTeamIdFetching,
    ]
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};
export const useApiMutation = <
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
  Init extends MaybeOptionalInit<
    ApiPaths[Path],
    ApiMethodForPath<Method, Path>
  >,
  Options extends Omit<
    UseMutationOptions<
      ApiSuccessResponse<Method, Path, Init>,
      ApiErrorResponse<Method, Path, Init>,
      Init
    >,
    'mutationKey' | 'mutationFn'
  >,
>(
  method: Method,
  path: Path,
  options?: Options & ApiRequestOptions,
  queryClient?: QueryClient
) => {
  const apiContext = useContext(context);
  const { isVrpAdmin } = useTeamRole();
  const { id: meId } = useMe();
  const { managedTeamRegions } = usePartnerRegion();
  const [{ dataRegion: teamDataRegion }] = useTeam();
  const {
    onSuccess: userOnSuccess,
    apiTarget: requestedApiTarget,
    hideAuditActionNotification,
    teamScoped: requestedTeamScope,
    ...restOptions
  } = (options ?? {}) as Options & ApiRequestOptions;
  const { api, apiHost, apiTarget, dataRegion, teamId, teamScoped } =
    getApiRequestContext(apiContext, {
      apiTarget: requestedApiTarget,
      teamScoped: requestedTeamScope,
    });

  return useMutation<
    ApiSuccessResponse<Method, Path, Init>,
    ApiErrorResponse<Method, Path, Init>,
    Init
  >(
    {
      mutationKey: [
        method,
        path,
        'response',
        apiHost,
        apiTarget,
        teamScoped,
        dataRegion,
        teamId,
      ],
      mutationFn: async (init) => {
        let mutationApi = api;

        const headers = new Headers(
          (
            init as
              | { headers?: ConstructorParameters<typeof Headers>[0] }
              | undefined
          )?.headers
        );

        const targetDataRegion = headers.get('x-data-region');

        // Partner sessions call the selected region's API. Team sessions call
        // their own team's regional API and have no selected region.
        const isDifferentDataRegion =
          targetDataRegion !== null &&
          targetDataRegion !== (apiContext.dataRegion ?? teamDataRegion);

        if (
          apiTarget === 'regional' &&
          isDifferentDataRegion &&
          headers.get('x-team-id') !== apiContext.teamId
        ) {
          const managedRegion = managedTeamRegions.find(
            (region) => region.dataRegion === targetDataRegion
          );

          if (!managedRegion) {
            throw new Error(
              'Regional cross-team mutation targets an unmanaged data region'
            );
          }

          mutationApi = apiContext.getRegionalCrossTeamApi(
            managedRegion.apiBaseUrl
          );
        }

        const mth = method.toUpperCase() as Uppercase<typeof method>;
        const fn = mutationApi[mth] as ClientMethodForPaths<
          ApiPaths,
          typeof method,
          MediaType
        >;
        const initParam = (
          init === undefined ? [] : [init as InitWithUnknowns<typeof init>]
        ) as InitParam<Init>;
        const res = await fn<Path, Init>(path, ...initParam);

        if (!isApiSuccessResponse<Method, Path, Init>(res)) {
          throw res.error;
        }

        return res;
      },
      // Ahead of `restOptions` so a caller whose endpoint has an idempotency
      // contract can opt back into retrying an unsafe method.
      retry: retryIdempotentMethod(method),
      ...restOptions,
      onSuccess: (data, variables, onMutateResult, context) => {
        const isActionNotificationEnabled = readLocalStorageValue<
          boolean | undefined
        >({
          key: getAuditActionNotificationStorageKey(meId),
        });

        if (
          isVrpAdmin &&
          isActionNotificationEnabled &&
          !hideAuditActionNotification
        ) {
          showAuditActionNotification({
            response: data.response,
          });
        }

        userOnSuccess?.(data, variables, onMutateResult, context);
      },
    },
    queryClient
  );
};

export const useApiClient = () => useContext(context).$api;
/** A client that never retries; for long awaits where a retry would outlive the caller's deadline. */
export const useSingleAttemptApiClient = () =>
  useContext(context).singleAttemptApi;
export const useAuthenticatedApiFetch = () =>
  useContext(context).authenticatedFetch;
export const useRegionalCrossTeamApiResolver = () =>
  useContext(context).getRegionalCrossTeamApi;
export const useApiHost = () => useContext(context).apiHost;
export const useApiTeamId = () => useContext(context).teamId;
export const useApiDataRegion = () => useContext(context).dataRegion;
export const useApiBaseUrl = () => useContext(context).baseUrl;
export const useApiIsTeamIdFetching = () =>
  useContext(context).isTeamIdFetching;
export const uesApiMutationBase: Client['useMutation'] = (...props) =>
  useContext(context).useMutation(...props);

export type ApiClient = ReturnType<typeof useApiClient>;

class ApiQueryError extends Error {
  constructor(readonly message: string) {
    super(message);
    this.name = 'ApiQueryError';
  }
}

const fetchStorybookQueryData = async <TData,>(
  storybookQueryKey: string,
  signal: AbortSignal,
  pageParam?: unknown
): Promise<TData> => {
  const url = new URL(
    `/storybook/${storybookQueryKey}`,
    window.location.origin
  );
  if (pageParam !== undefined) {
    url.searchParams.set('pageParam', JSON.stringify(pageParam));
  }

  const response = await fetch(url, { signal });

  if (!response.ok) {
    const error = await response.json().catch(() => undefined);

    if (error && typeof error === 'object') throw error;

    throw new Error(
      response.statusText ||
        `Storybook query failed with status ${response.status}`
    );
  }

  return response.json() as Promise<TData>;
};

/**
 * PRO-17355: TanStack consults `retry` only after the query function's rejection
 * settles, so anything reported from that rejection fires on every attempt of a
 * failure the next attempt may still recover from — and a transient transport
 * blip became one error-level Sentry event per mounted query.
 *
 * `fetchFailureCount` is reset per fetch and holds exactly the value TanStack is
 * about to pass to `retry`, so the same predicate decides whether this failure
 * is terminal. A failure that will be retried, or that the retryer will pause
 * until the browser reconnects, is not yet an error for the user to see.
 */
const getTerminalQueryAttemptCount = (
  client: QueryClient,
  queryKey: TanstackQueryKey,
  error: ApiClientError,
  queryRetry?: UseQueryOptions<unknown, ApiClientError>['retry']
) => {
  const failureCount = client.getQueryState(queryKey)?.fetchFailureCount ?? 0;
  const retryValue = queryRetry ?? retry;
  const shouldRetry =
    retryValue === true ||
    (typeof retryValue === 'number' && failureCount < retryValue) ||
    (typeof retryValue === 'function' && retryValue(failureCount, error));

  return shouldRetry ? undefined : failureCount + 1;
};

function handleQueryError(
  error: ApiClientError,
  signal: AbortSignal,
  opts: {
    hideNotification: boolean;
    queryGroup: QueryGroup;
    sentryQueryKey?: string;
    title?: string;
  },
  extra: {
    apiHost: string;
    attemptCount: number;
    isOnline: boolean;
  }
) {
  const title = opts.title || 'Query Error';
  const message = error?.message;

  const errorCode = error.code?.toLowerCase();
  const isNotFound = errorCode === 'not_found';
  const isServerError =
    errorCode === 'service_unavailable' ||
    errorCode === 'internal_server_error';

  // Session loss belongs to the auth flow; malformed credentials remain reportable.
  if (
    !message ||
    signal?.aborted ||
    isNotFound ||
    error.name === 'ApiSessionUnavailableError'
  )
    return;

  const lower = message.toLowerCase();
  const isMalformedJwtMessage =
    lower.includes('could not verify jwt') ||
    lower.includes('compactdecodeerror') ||
    lower.includes('invalid number of parts');
  const isClerkOfflineError = isClerkOfflineErrorMessage(message);
  const isClerkSessionUnavailableError =
    isClerkSessionUnavailableErrorMessage(message);
  const transportErrorKind = getTransportErrorKind(error);
  // PRO-18816: evaluating the policy claims the shared per-host window, so a
  // query that never reports this error must not suppress the next one that does.
  const isCapturableQueryError = shouldCaptureApiQueryError(
    error,
    opts.sentryQueryKey
  );
  const transportCapture =
    transportErrorKind && isCapturableQueryError
      ? transportErrorCapturePolicy.evaluate({
          errorKind: transportErrorKind,
          host: extra.apiHost,
          isOnline: extra.isOnline,
        })
      : undefined;

  // PRO-17358: the API client already handled this rejection centrally — it
  // refreshed the credential and replayed the request, and reported the failure
  // once if the fresh credential was still rejected. Repeating it per query only
  // duplicates that diagnostic. Scoped to the exact rejection the client
  // recovers from, so an unrecovered 401 still reaches a toast and Sentry.
  const isHandledSessionError = isInvalidJwtApiError(error);

  const disableNotification =
    lower.includes('failed to fetch') ||
    lower.includes('load failed') ||
    lower.includes('database query error') ||
    lower.includes('no api access') ||
    isClerkOfflineError ||
    isClerkSessionUnavailableError ||
    isHandledSessionError ||
    isMalformedJwtMessage;

  const isLocalhostApi =
    typeof extra.apiHost === 'string' && extra.apiHost.includes('localhost');

  const disableSentry =
    lower.includes('load failed') ||
    lower.includes('signal is aborted without reason') ||
    isLocalhostApi ||
    isServerError ||
    isClerkOfflineError ||
    isClerkSessionUnavailableError ||
    isHandledSessionError ||
    isMalformedJwtMessage ||
    transportCapture?.shouldCapture === false ||
    !isCapturableQueryError;

  if (!disableSentry) {
    const sentryMetadata = getApiQueryErrorSentryMetadata({
      errorCode:
        error.code ||
        (error.name === 'ApiInvalidAccessTokenError'
          ? 'INVALID_ACCESS_TOKEN'
          : undefined),
      // The authenticated fetch rewrites an empty error body into this message
      // and status.
      emptyResponseStatus:
        'status' in error &&
        typeof error.status === 'number' &&
        message.endsWith('(empty response body).')
          ? error.status
          : undefined,
      message,
      queryGroup: opts.queryGroup,
      sentryQueryKey: opts.sentryQueryKey,
    });

    captureSentryError(new ApiQueryError(message), {
      // Terminal transport failures carry no API error code, so the default
      // metadata would group them with any other code-less error of the same
      // query group. Mirror the Hasura transport fingerprint instead. An API
      // error with a code keeps its code grouping even if a nested cause
      // looks like a transport failure.
      fingerprint:
        transportErrorKind && !error.code
          ? ['api-query-transport-terminal']
          : sentryMetadata.fingerprint,
      level: 'error',
      tags: {
        ...sentryMetadata.tags,
        ...(transportErrorKind
          ? {
              error_kind: 'transport',
              transport_error_kind: transportErrorKind,
              transport_host: extra.apiHost,
            }
          : {}),
      },
      extra: {
        ...extra,
        apiError: error,
        // Sentry truncates `apiError.context` at its default depth.
        apiErrorContext: error.context,
        ...(transportCapture
          ? {
              suppressed_transport_error_count:
                transportCapture.suppressedCount,
              transport_error_window_ms: transportCapture.windowMs,
            }
          : {}),
      },
    });
  }

  if (opts.hideNotification || disableNotification) {
    // do not show notification to user
    return;
  }

  if (lower.includes('exceeded the time limit of 60s')) {
    showWarnNotification({
      title: 'Connection Timeout',
      message: `Due to a possible slow internet connection, or temporarily high load on our servers, the connection from your computer has timed out. Please try again later.`,
    });
    return;
  }

  showApiErrorNotification({
    error,
    title: title,
    defaultMessage:
      'We failed to fetch your data. Please try again later and contact support if the issue persists. ',
  });
}

export function useApiQuery<TData, TArgs extends Record<string, unknown>>(
  fn: (api: ApiClient, signal: AbortSignal, variables: TArgs) => Promise<TData>,
  options: {
    skip?: boolean;
    variables: TArgs;
    queryGroup: QueryGroup;
    uniqueQueryKey: string;
    sentryQueryKey?: string;
    queryKeys?: QueryKey[];
    keepPreviousData?: boolean;
    errorTitle?: string;
    hideNotification?: boolean;
    staleTime?: number;
    storybookQueryKey?: string;
    refetchOnMount?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnWindowFocus?: boolean;
    gcTime?: number;
    apiTarget?: ApiTarget;
    regionalApiBaseUrl?: string;
    retry?: UseQueryOptions<TData, ApiClientError>['retry'];
    teamScoped?: boolean;
    poll?: Omit<
      PollUntilCompletedProps,
      'refetchQuery' | 'skip' | 'isCompleted' | 'interval'
    > & {
      isCompleted: (data: TData | undefined) => boolean;
      interval?: number | ((data: TData | undefined) => number);
    };
  }
) {
  const { online: isOnline } = useNetwork();

  const apiContext = useContext(context);

  const isFetchingTeam = useApiIsTeamIdFetching();
  const {
    api: $api,
    apiHost,
    dataRegion,
    teamId,
    teamScoped: isTeamScoped,
  } = getApiRequestContext(apiContext, options);
  const shouldGateTeamQuery = isTeamScoped && isFetchingTeam;

  const queryKey = useMemo(
    () => [
      options.queryGroup,
      options.uniqueQueryKey,
      apiHost,
      options.variables,
      options.storybookQueryKey,
      ...(options.queryKeys || []),
      options.hideNotification,
      ...(isTeamScoped ? [...(dataRegion ? [dataRegion] : []), teamId] : []),
    ],
    [
      options.queryGroup,
      options.uniqueQueryKey,
      apiHost,
      options.variables,
      options.storybookQueryKey,
      options.queryKeys,
      options.hideNotification,
      isTeamScoped,
      teamId,
      dataRegion,
    ]
  );

  const queryData = useTanstackQuery<TData, ApiClientError>({
    queryKey,
    placeholderData: options.keepPreviousData
      ? isTeamScoped
        ? (previousData, previousQuery) => {
            const previousMeta = previousQuery?.meta;
            return previousMeta?.apiHost === apiHost &&
              previousMeta?.dataRegion === dataRegion &&
              previousMeta?.teamId === teamId
              ? previousData
              : undefined;
          }
        : (previousData, previousQuery) =>
            previousQuery?.meta?.apiHost === apiHost ? previousData : undefined
      : undefined,
    staleTime:
      options.staleTime ?? (options.keepPreviousData ? 30_000 : undefined),
    gcTime: options.gcTime,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    refetchOnReconnect: options.refetchOnReconnect,
    refetchOnMount: options.refetchOnMount,
    enabled: !options.skip && !shouldGateTeamQuery,
    meta: {
      apiHost,
      dataRegion: isTeamScoped ? dataRegion : undefined,
      regionScoped: isTeamScoped && !!dataRegion,
      teamId: isTeamScoped ? teamId : undefined,
      teamScoped: isTeamScoped,
    },
    retry: options.retry ?? retry,
    queryFn: ({ client, signal }) => {
      if (isStorybook && options.storybookQueryKey) {
        return fetchStorybookQueryData<TData>(
          options.storybookQueryKey,
          signal
        );
      }

      return fn($api, signal, options.variables).catch(
        (error: ApiClientError) => {
          const attemptCount = getTerminalQueryAttemptCount(
            client,
            queryKey,
            error,
            options.retry
          );

          if (attemptCount !== undefined) {
            handleQueryError(
              error,
              signal,
              {
                hideNotification: !!options.hideNotification,
                queryGroup: options.queryGroup,
                sentryQueryKey: options.sentryQueryKey,
                title: options.errorTitle,
              },
              {
                attemptCount,
                isOnline,
                apiHost,
              }
            );
          }
          throw error;
        }
      );
    },
  });

  const isCompleted = useMemo(() => {
    return !!options?.poll?.isCompleted(queryData.data);
  }, [queryData.data, options?.poll?.isCompleted]);

  usePollUntilCompleted(
    options?.poll
      ? {
          isCompleted,
          skip: !!options.skip || shouldGateTeamQuery,
          interval:
            typeof options.poll.interval === 'function'
              ? options.poll.interval(queryData.data)
              : options.poll.interval,
          onCompleted: options.poll.onCompleted,
          refetchQuery: queryData.refetch,
        }
      : undefined
  );

  return { ...queryData, queryKey };
}

export function useInfiniteApiQuery<
  TData,
  TArgs extends Record<string, unknown>,
  TPageParam = number,
>(
  fn: (
    api: ApiClient,
    signal: AbortSignal,
    variables: TArgs,
    pageParam: TPageParam
  ) => Promise<TData>,
  options: {
    skip?: boolean;
    variables: TArgs;
    queryGroup: QueryGroup;
    uniqueQueryKey: string;
    sentryQueryKey?: string;
    queryKeys?: QueryKey[];
    errorTitle?: string;
    hideNotification?: boolean;
    refetchOnMount?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    gcTime?: number;
    storybookQueryKey?: string;
    initialPageParam: TPageParam;
    getNextPageParam: (
      lastPage: TData,
      allPages: TData[],
      lastPageParam: TPageParam,
      allPageParams: TPageParam[]
    ) => TPageParam | undefined | null;
    getPreviousPageParam?: (
      firstPage: TData,
      allPages: TData[],
      firstPageParam: TPageParam,
      allPageParams: TPageParam[]
    ) => TPageParam | undefined | null;
    maxPages?: number;
    keepPreviousData?: boolean;
    apiTarget?: ApiTarget;
    regionalApiBaseUrl?: string;
    teamScoped?: boolean;
  }
) {
  const { online: isOnline } = useNetwork();

  const apiContext = useContext(context);

  const isFetchingTeam = useApiIsTeamIdFetching();
  const {
    api: $api,
    apiHost,
    dataRegion,
    teamId,
    teamScoped: isTeamScoped,
  } = getApiRequestContext(apiContext, options);

  const queryKey = useMemo(
    () => [
      options.queryGroup,
      options.uniqueQueryKey,
      apiHost,
      options.variables,
      options.storybookQueryKey,
      ...(options.queryKeys || []),
      options.hideNotification,
      ...(isTeamScoped ? [...(dataRegion ? [dataRegion] : []), teamId] : []),
    ],
    [
      options.queryGroup,
      options.uniqueQueryKey,
      apiHost,
      options.variables,
      options.storybookQueryKey,
      options.queryKeys,
      options.hideNotification,
      isTeamScoped,
      teamId,
      dataRegion,
    ]
  );

  const queryData = useTanstackInfiniteQuery<
    TData,
    ApiClientError,
    InfiniteData<TData>,
    TanstackQueryKey,
    TPageParam
  >({
    queryKey,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    refetchOnReconnect: options.refetchOnReconnect,
    staleTime: options.staleTime,
    gcTime: options.gcTime,
    enabled: !options.skip && (!isTeamScoped || (!!teamId && !isFetchingTeam)),
    initialPageParam: options.initialPageParam,
    getNextPageParam: options.getNextPageParam,
    getPreviousPageParam: options.getPreviousPageParam,
    refetchOnMount: options.refetchOnMount ?? true,
    maxPages: options.maxPages,
    placeholderData: isTeamScoped
      ? (previousData, previousQuery) => {
          const previousMeta = previousQuery?.meta;
          return previousMeta?.apiHost === apiHost &&
            previousMeta?.dataRegion === dataRegion &&
            previousMeta?.teamId === teamId
            ? previousData
            : undefined;
        }
      : (previousData, previousQuery) =>
          previousQuery?.meta?.apiHost === apiHost ? previousData : undefined,
    meta: {
      apiHost,
      dataRegion: isTeamScoped ? dataRegion : undefined,
      regionScoped: isTeamScoped && !!dataRegion,
      teamId: isTeamScoped ? teamId : undefined,
      teamScoped: isTeamScoped,
    },
    retry,
    queryFn: ({ client, signal, pageParam }) => {
      if (isStorybook && options.storybookQueryKey) {
        return fetchStorybookQueryData<TData>(
          options.storybookQueryKey,
          signal,
          pageParam
        );
      }

      return fn($api, signal, options.variables, pageParam as TPageParam).catch(
        (error: ApiClientError) => {
          const attemptCount = getTerminalQueryAttemptCount(
            client,
            queryKey,
            error
          );

          if (attemptCount !== undefined) {
            handleQueryError(
              error,
              signal,
              {
                hideNotification: !!options.hideNotification,
                queryGroup: options.queryGroup,
                sentryQueryKey: options.sentryQueryKey,
                title: options.errorTitle,
              },
              {
                attemptCount,
                isOnline,
                apiHost,
              }
            );
          }
          throw error;
        }
      );
    },
  });

  return { ...queryData, queryKey };
}
